import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import isEqual from 'lodash.isequal';
import {
  BaseTransaction,
  TransactionAML,
  Response,
  TransactionWarning,
  GeneralLedgerTransaction,
  TransactionWarningTypeEnum,
  IntegrationLog,
  Pagination,
} from '@ui/src';
import { OutgoingTransactionValidator } from '../../types';
import { outgoingTransactionsListApi } from '../services';
import { statusesTransactionsApi } from '../../../../store/statuses/services';
import { transactionApi } from '../../../transactions/store/services';
import { WarningsInfo } from '../../../transactions/type';

export interface OutgoingTransactionState {
  panelIsOpen: boolean;
  transactionId?: string;
  transaction?: BaseTransaction;
  aml?: TransactionAML;
  validators?: OutgoingTransactionValidator;
  warnings?: TransactionWarning[];
  glTransactions?: GeneralLedgerTransaction[];
  warningsInfo?: WarningsInfo;
  integrationLogs?: IntegrationLog[];
  integrationLogsPagination?: Pagination;
}

const initialState: OutgoingTransactionState = {
  panelIsOpen: true,
  transaction: undefined,
};

export const outgoingTransactionSlice = createSlice({
  name: 'outgoingTransaction',
  initialState,
  reducers: {
    changePanelState: (
      state,
      action: PayloadAction<{ state: boolean; transactionId?: string }>,
    ) => {
      state.panelIsOpen = action.payload.state;

      if (action.payload.state) {
        state.transactionId = action.payload.transactionId;
      } else {
        state.transactionId = undefined;
      }
    },
    setWarnings: (state, action: PayloadAction<TransactionWarning[]>) => {
      state.warnings = action.payload;
    },
    clearOutgoingTransactionState: (state) => {
      state.transaction = initialState.transaction;
      state.transactionId = undefined;
      state.panelIsOpen = false;
      state.warnings = undefined;
      state.warningsInfo = undefined;
      state.integrationLogs = undefined;
      state.integrationLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionById
          .matchFulfilled,
        (state, action: PayloadAction<{ data: BaseTransaction }>) => {
          const prevTransaction = state.transaction;
          const nextTransaction = action.payload.data;

          if (!prevTransaction) {
            state.transaction = {
              ...nextTransaction,
            };
          } else if (!isEqual(prevTransaction, nextTransaction)) {
            state.transaction = {
              ...nextTransaction,
            };
          }
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionWarningsInfo
          .matchFulfilled,
        (state, action: PayloadAction<{ data: WarningsInfo }>) => {
          state.warningsInfo = action.payload.data;
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionValidators
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: OutgoingTransactionValidator }>,
        ) => {
          state.validators = action.payload.data;
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionAmlById
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionAML>>) => {
          const prevAml = state.aml;
          const nextAml =
            action.payload.data.edges.map((item) => item.node)?.[0] ||
            undefined;

          if (!prevAml) {
            state.aml = nextAml;
          } else if (!isEqual(prevAml, nextAml)) {
            state.aml = nextAml;
          }
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionWarnings
          .matchFulfilled,
        (state, action: PayloadAction<Response<TransactionWarning>>) => {
          const prevWarnings = state.warnings;
          const nextWarnings = action.payload.data.edges.map(
            (item) => item.node,
          );

          if (!prevWarnings) {
            state.warnings = nextWarnings;
          } else if (!isEqual(prevWarnings, nextWarnings)) {
            state.warnings = nextWarnings?.map((item) => {
              const result = { ...item };

              const currentPrevWarning = prevWarnings?.find(
                (prevWarning) => prevWarning?.id === result.id,
              );

              if (
                currentPrevWarning &&
                (currentPrevWarning?.blacklistDoc ||
                  currentPrevWarning?.blacklistItem)
              ) {
                result.blacklistDoc = currentPrevWarning.blacklistDoc;
                result.blacklistItem = currentPrevWarning.blacklistItem;
              }

              if (
                currentPrevWarning &&
                currentPrevWarning?.relatedTransactions
              ) {
                result.relatedTransactions =
                  currentPrevWarning.relatedTransactions;
              }

              return result;
            });
          }
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints.getOutgoingTransactionWarning
          .matchFulfilled,
        (
          state,
          action: PayloadAction<{ data: TransactionWarning }> & {
            meta: {
              arg: {
                originalArgs: {
                  warningId: string;
                };
              };
            };
          },
        ) => {
          const nextWarnings = state.warnings?.map((item) => {
            const result = { ...item };

            if (result.id === action.meta.arg.originalArgs.warningId) {
              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.BLACKLIST
              ) {
                result.blacklistDoc = action.payload.data.blacklistDoc;
                result.blacklistItem = action.payload.data.blacklistItem;
              }

              if (
                action.payload.data.type ===
                TransactionWarningTypeEnum.MONITORING
              ) {
                result.relatedTransactions =
                  action.payload.data.relatedTransactions;
              }
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      )
      .addMatcher(
        outgoingTransactionsListApi.endpoints
          .applyOutgoingTransactionWarningAction.matchFulfilled,
        (state, action: PayloadAction<{ data: TransactionWarning }>) => {
          const nextWarnings = state.warnings?.map((item) => {
            let result = { ...item };

            if (action.payload.data.id === result.id) {
              result = {
                ...action.payload.data,
                blacklistDoc: result.blacklistDoc,
                blacklistItem: result.blacklistItem,
                relatedTransactions: result.relatedTransactions || [],
              };
            }

            return result;
          });

          state.warnings = nextWarnings;
        },
      );
    builder.addMatcher(
      statusesTransactionsApi.endpoints.changeTransactionStatus.matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        if (state.transaction) {
          const nextTransaction = { ...state.transaction };

          nextTransaction.baseellaStatus = action.payload.data.baseellaStatus;
          nextTransaction.latestQueuedStatuses = action.payload.data
            .latestQueuedStatuses
            ? action.payload.data.latestQueuedStatuses
            : nextTransaction.latestQueuedStatuses;

          state.transaction = nextTransaction;
        }
      },
    );
    builder.addMatcher(
      outgoingTransactionsListApi.endpoints.updateOutgoingTransaction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: BaseTransaction }>) => {
        state.transaction = {
          ...state.transaction,
          ...action.payload.data,
        };
      },
    );
    builder.addMatcher(
      transactionApi.endpoints.getGeneralLedgerTransactionValues.matchFulfilled,
      (state, action: PayloadAction<Response<GeneralLedgerTransaction>>) => {
        if (state.transaction) {
          state.glTransactions = action.payload.data.edges.map(
            (item) => item.node,
          );
        }
      },
    );
    builder.addMatcher(
      outgoingTransactionsListApi.endpoints.getOutgoingIntegrationLogs
        .matchFulfilled,
      (state, action: PayloadAction<Response<IntegrationLog>>) => {
        if (state.transaction) {
          state.integrationLogs = action.payload.data.edges.map(
            (item) => item.node,
          );

          state.integrationLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
  },
});

export const { changePanelState, clearOutgoingTransactionState, setWarnings } =
  outgoingTransactionSlice.actions;

export default outgoingTransactionSlice.reducer;
